/* Live video from a node's camera: the same path as live sound.
 *
 * node -> object storage -> browser, with the receiver minting a playlist of
 * presigned URLs and never touching a byte. The camera's stream is fMP4: its
 * H.264 copied straight from the hardware encoder, with the hydrophone on the
 * left channel and the HydroMoth on the right. So this is hls.js on a <video>
 * rather than the audio hook's <audio> and WebAudio graph -- there is nothing
 * here to filter or measure, it is for looking.
 *
 * Started muted, because a browser lets a muted video play without a gesture
 * of its own and the gesture here was spent on writing desired state, several
 * seconds ago. Unmuting is one press.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type HlsType from 'hls.js'
import { CAMERA_STREAM } from './api'
import {
  HLS_MAX_RECOVERIES, hlsBackoff, hlsXhrSetup, loadHls, type LiveStatus,
} from './live'

export interface LiveVideo {
  /** Put this on the <video> element. */
  ref: React.MutableRefObject<HTMLVideoElement | null>
  status: LiveStatus
  error: string | null
  /** Seconds behind the live edge, once a fragment has played. */
  latency: number | null
  start: () => void
  stop: () => void
  muted: boolean
  setMuted: (m: boolean) => void
}

export function useLiveVideo(missionId: string | null,
                             stream = CAMERA_STREAM): LiveVideo {
  const ref = useRef<HTMLVideoElement | null>(null)
  const hlsRef = useRef<HlsType | null>(null)
  // Bumped by every start and stop, so a start whose import settles after
  // Stop was pressed knows it is no longer wanted.
  const gen = useRef(0)
  const retryRef = useRef<number | undefined>(undefined)
  const [status, setStatus] = useState<LiveStatus>('idle')
  const [error, setError] = useState<string | null>(null)
  const [latency, setLatency] = useState<number | null>(null)
  const [muted, setMutedState] = useState(true)

  const teardown = useCallback(() => {
    window.clearTimeout(retryRef.current)
    hlsRef.current?.destroy()
    hlsRef.current = null
    const v = ref.current
    if (v) {
      v.pause()
      v.removeAttribute('src')
      v.load()
    }
  }, [])

  const stop = useCallback(() => {
    gen.current++
    teardown()
    setStatus('idle')
    setLatency(null)
  }, [teardown])

  const start = useCallback(async () => {
    const video = ref.current
    if (!video || !missionId) return
    teardown()
    const mine = ++gen.current
    const current = () => gen.current === mine
    setError(null)
    setLatency(null)
    setStatus('starting')

    video.muted = muted
    // The element says when pictures are actually moving, which is the only
    // honest meaning of "playing" -- play() resolving says nothing of it.
    video.onplaying = () => { if (current()) setStatus('playing') }
    video.onwaiting = () => {
      if (current()) setStatus((s) => (s === 'playing' ? 'stalled' : s))
    }

    const url = `/v1/missions/${encodeURIComponent(missionId)}/live.m3u8` +
      `?stream=${encodeURIComponent(stream)}`
    const fail = (msg: string) => {
      if (!current()) return
      setError(msg)
      setStatus('error')
    }

    const Hls = await loadHls()
    if (!current()) return

    if (!Hls.isSupported()) {
      // Safari plays HLS itself, cookies and all.
      if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = url
        void video.play().catch((e) => fail(`playback blocked: ${e}`))
      } else {
        fail('This browser cannot play live video.')
      }
      return
    }

    const hls = new Hls({
      // Not low-latency mode: the camera's segments are cut on keyframes and
      // run one to two seconds, and a buffer that thin stutters on every long
      // one. Three segments behind is still well under ten seconds.
      liveSyncDurationCount: 3,
      maxBufferLength: 10,
      backBufferLength: 10,
      manifestLoadingTimeOut: 12000,
      xhrSetup: hlsXhrSetup,
    })
    hlsRef.current = hls

    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      void video.play().catch((e) => fail(`playback blocked: ${e}`))
    })

    let netTries = 0
    let mediaTries = 0
    const later = (fn: () => void, delay: number) => {
      window.clearTimeout(retryRef.current)
      retryRef.current = window.setTimeout(() => { if (current()) fn() }, delay)
    }
    hls.on(Hls.Events.ERROR, (_evt, data) => {
      if (!current()) return
      const details = String(data.details)
      if (!data.fatal) {
        if (details === 'bufferStalledError') setStatus('stalled')
        return
      }
      switch (data.type) {
        case Hls.ErrorTypes.NETWORK_ERROR:
          if (details === 'manifestLoadError') {
            fail('No playlist. The camera may not be streaming yet.')
          } else if (netTries >= HLS_MAX_RECOVERIES) {
            fail(`Lost the stream and could not pick it up again. (${details})`)
          } else {
            setStatus('stalled')
            later(() => hls.startLoad(), hlsBackoff(netTries++))
          }
          break
        case Hls.ErrorTypes.MEDIA_ERROR:
          // Also what a camera restart looks like: a new init segment under
          // fragments from the old session, for one playlist refresh.
          if (mediaTries >= HLS_MAX_RECOVERIES) {
            fail(`The video could not be decoded. (${details})`)
          } else {
            setStatus('stalled')
            later(() => hls.recoverMediaError(), hlsBackoff(mediaTries++))
          }
          break
        default:
          fail(details || 'stream failed')
      }
    })

    hls.on(Hls.Events.FRAG_CHANGED, () => {
      const l = hls.latency
      if (Number.isFinite(l)) setLatency(l)
      netTries = 0
      mediaTries = 0
    })

    hls.loadSource(url)
    hls.attachMedia(video)
  }, [missionId, stream, muted, teardown])

  const setMuted = useCallback((m: boolean) => {
    setMutedState(m)
    if (ref.current) ref.current.muted = m
  }, [])

  // Leaving the page stops the player. Stopping the NODE is the caller's job,
  // because only the caller knows whether it was the one that started it.
  useEffect(() => () => { gen.current++; teardown() }, [teardown])

  return {
    ref, status, error, latency, muted, setMuted,
    start: () => { void start() },
    stop,
  }
}
